/**
 * SampleHandInfo - InfoButton body for the sample hand simulator
 */
import type { ReactElement } from 'react';

import { InfoList, InfoSection, InfoTip } from '@/components/InfoButton/InfoButton';

export default function SampleHandInfo(): ReactElement {
  return (
    <>
      <InfoSection title="What is this?">
        <p>
          Draw sample opening hands from your main deck and see whether they are actually playable — enough mana, the right colors, and
          something to do early. Commanders and companions start in the command zone and are never shuffled into the library.
        </p>
      </InfoSection>

      <InfoSection title="London mulligan">
        <InfoList
          items={[
            'Each mulligan draws a fresh 7 and raises the bottoming cost by 1',
            'When you keep, pick that many cards to put on the bottom',
            'The hand then shrinks to its real size (7 − mulligans)',
            'Mulligans stop at 4 cards — below that the exercise stops being useful',
          ]}
        />
      </InfoSection>

      <InfoSection title="How castability is judged">
        <InfoList
          items={[
            'Each colored pip must be paid by a different mana source in hand',
            'Lands are assumed to arrive one per turn from the hand — draws are ignored',
            'Because draws are ignored, "first play" is a worst-case estimate',
            'A spell that is only too expensive for the lands in hand is not a fault — you draw more lands',
            'Hybrid and {C} pips are treated as generic mana',
          ]}
        />
      </InfoSection>

      <InfoSection title="Cards with more than one face">
        <p>
          Adventures, split cards and modal double-faced cards count as castable from any of their faces, so the cheap half counts as an
          early play and the card is only colour-screwed when <em>every</em> face is.
        </p>
        <InfoList
          items={[
            'Adventure — the Adventure half and the creature are both castable',
            'Split — each half is priced separately instead of using the combined cost',
            'Modal DFC — the front face; a land back is played, not cast, and is not modelled',
            'Transform backs are never castable, so they add no mode',
            'Aftermath halves are skipped — they are only castable from the graveyard',
            'The turn timeline names the face that was actually cast',
          ]}
        />
      </InfoSection>

      <InfoSection title="Mana rock scoring">
        <p>Mana rocks contribute fractional land equivalents based on cost and mana produced. A rock you can&apos;t cast counts as 0.</p>
        <InfoList
          items={[
            'Sol Ring with 1 land → +0.76 effective',
            '2-cost rock with 2 lands → +0.33 effective',
            '3-cost rock with 3 lands → +0.25 effective',
            'Rock without enough lands to cast → +0.0',
          ]}
        />
      </InfoSection>

      <InfoSection title="Verdicts">
        <InfoList
          items={[
            'Keep — meets every keep rule',
            'Risky — some spells need colors this hand can\u2019t make, or the first play is late',
            'Mulligan — below the source floor, flooded, all lands, or every spell is off-color',
            'Color screw is about colors only — an expensive spell you can\u2019t cast yet is not a fault',
            'Thresholds come from the shared Simulation settings bar at the top of the panel',
          ]}
        />
      </InfoSection>

      <InfoSection title="Turn simulation">
        <p>
          After keeping, step the hand forward one turn at a time. Each turn draws a card, plays the best available land, then casts
          greedily from most to least expensive until nothing else fits. Every card that entered or left your hand that turn is shown.
        </p>
        <InfoList
          items={[
            'Blue ring — drawn this turn · green — played as a land · purple — cast',
            'Land choice favours the colour the cheapest uncast face needs, then the most still-missing colours',
            'Casting is greedy, not optimal — it will not skip a big spell so two small ones fit',
            'A turn is stalled when no land was playable and nothing was cast',
            'Nothing else is modelled: no ramp, tutors, card draw, scry, cost reduction, triggers or opponents',
            'Cards you put on the bottom are treated as shuffled back into the library',
            'Turn order and turn count live in the Simulation settings bar',
          ]}
        />
      </InfoSection>

      <InfoSection title="Keyboard shortcuts">
        <InfoList items={['N — new hand', 'M — mulligan', 'K — keep', 'U — undo', 'T — next turn (after keeping)']} />
      </InfoSection>

      <InfoTip>
        Draw 10+ hands to get a feel for how your deck plays. The history pips keep a running keep rate — if you are mulliganing more than
        30% of the time, revisit your mana base or curve.
      </InfoTip>
    </>
  );
}
